import { Link, useLocation } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Card, PageHero } from "@/components/Card";
import { Pill } from "@/components/Pill";
import { ShieldAlert } from "lucide-react";
import { MerchantRole, useAuth } from "@/hooks/useAuth";

/**
 * Shown when a user hits a route they don't have the role for. Soft wall,
 * not an error page — the user is signed in and their account is fine; they
 * just can't do this thing.
 */
export default function NoPermission() {
  const { currentRole, currentMerchant } = useAuth();
  const state = (useLocation().state ?? {}) as { required?: MerchantRole[]; from?: { pathname?: string } };
  const required = state.required ?? [];

  return (
    <AppLayout>
      <div className="rf-stagger space-y-6">
        <PageHero
          eyebrow="Access"
          title={<>Not available on your <span className="italic">role</span></>}
          subtitle={
            currentMerchant
              ? `You're viewing ${currentMerchant.name} as ${currentRole ?? "a non-member"}.`
              : "You don't have an active workspace."
          }
        />
        <Card className="flex items-start gap-4 px-5 py-5">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-amber/15 text-amber-rf">
            <ShieldAlert className="h-[18px] w-[18px]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[14px] text-ink">
              This page requires{" "}
              {required.length > 0 ? (
                required.map((r, i) => (
                  <span key={r}>
                    <Pill tone="neutral">{r}</Pill>
                    {i < required.length - 1 ? " or " : ""}
                  </span>
                ))
              ) : (
                <Pill tone="neutral">a higher role</Pill>
              )}
              .
            </div>
            <p className="mt-2 text-[13px] text-ink-2">
              Ask an owner of this workspace to promote you, or switch to a workspace where you
              have access.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to="/"
                className="rounded-md bg-ink px-4 py-2 text-[13px] text-canvas transition-opacity duration-[120ms] hover:opacity-90"
              >
                Back to overview
              </Link>
              <Link
                to="/settings"
                className="rounded-md border border-line bg-canvas px-4 py-2 text-[13px] text-ink transition-colors duration-[120ms] hover:bg-canvas-2"
              >
                Settings
              </Link>
            </div>
          </div>
        </Card>
      </div>
    </AppLayout>
  );
}
